/** Who loses a licence when the grace periods close (L1).
 *
 *  A subscription past its end date keeps working for a short grace period.
 *  Where a SKU has more assignments than purchased seats, the difference is
 *  living on that borrowed capacity: on the day it expires, that many people
 *  lose the licence. This is the one number on the board that is measured in
 *  people rather than money, which is why it gets a panel of its own instead
 *  of a caption on the waste chart.
 *
 *  Form: a sorted horizontal bar, because the job is comparing magnitudes
 *  across named categories (design rules §2). One series, one colour — and the
 *  colour is `danger` because it is a status, not a category: these
 *  assignments are broken on a date certain. Never colour-alone — the axis
 *  names each SKU and the table twin carries every figure.
 */

import { Chart, type ChartConfig } from "@norican/dataviz";
import { AlertTriangle, Icon } from "@norican/icons";
import { type DataGridColumnDef, EmptyState } from "@norican/react";

import { ChartCard, TABLE_TWIN_PARAM } from "@/components/board";
import type { LicenseSkuRow, LicenseTotals } from "@/lib/api";
import { useDrawerParam } from "@/lib/drill";

import { formatSeats, SEAT_LABEL } from "./licence-format";

/** `?data=grace-risk` opens this chart's table twin. */
const TWIN_ID = "grace-risk";

/** An L1 ranking shows the worst few; the twin carries the rest. */
const TOP_LIMIT = 10;

/** The backend rebuilds its snapshot every 15 minutes; flag anything older. */
const STALE_AFTER_MS = 15 * 60_000;

/** One series, danger-toned: a status colour for a status (design rules §3.2). */
const RISK_CONFIG: ChartConfig = {
  over_assigned: { label: SEAT_LABEL.overAssigned, color: "var(--norican-danger)" },
};

interface GraceRiskPanelProps {
  /** SKUs with more assignments than purchased seats, worst first. */
  rows: LicenseSkuRow[];
  totals: LicenseTotals;
  generatedAt: string | null | undefined;
}

export function GraceRiskPanel({ rows, totals, generatedAt }: GraceRiskPanelProps) {
  const twin = useDrawerParam(TABLE_TWIN_PARAM);

  // With no expiring capacity anywhere there is no grace period to lose, and
  // an always-on empty panel is just furniture.
  if (totals.grace_period_seats === 0 && rows.length === 0) {
    return null;
  }

  const ranked = [...rows].sort((a, b) => b.over_assigned - a.over_assigned);
  const barRows = ranked.slice(0, TOP_LIMIT);

  const twinColumns: DataGridColumnDef<LicenseSkuRow>[] = [
    {
      id: "name",
      header: "SKU",
      accessor: (row) => row.name,
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-fg font-medium">{row.name}</span>
          <span className="text-fg-muted text-xs">{row.sku_part_number}</span>
        </div>
      ),
    },
    {
      id: "over_assigned",
      header: SEAT_LABEL.overAssigned,
      numeric: true,
      accessor: (row) => row.over_assigned,
      cell: (row) => (
        <span className="text-danger-fg font-medium tabular-nums">
          {formatSeats(row.over_assigned)}
        </span>
      ),
    },
    {
      id: "consumed",
      header: SEAT_LABEL.assigned,
      numeric: true,
      accessor: (row) => row.consumed,
      cell: (row) => <span className="text-fg tabular-nums">{formatSeats(row.consumed)}</span>,
    },
    {
      id: "purchased",
      header: SEAT_LABEL.purchased,
      numeric: true,
      accessor: (row) => row.purchased,
      cell: (row) => <span className="text-fg tabular-nums">{formatSeats(row.purchased)}</span>,
    },
    {
      id: "grace",
      header: SEAT_LABEL.grace,
      numeric: true,
      accessor: (row) => row.grace,
      cell: (row) => <span className="text-warning-fg tabular-nums">{formatSeats(row.grace)}</span>,
    },
  ];

  return (
    <ChartCard
      title="Licences that end with the grace period"
      caption={
        ranked.length > TOP_LIMIT
          ? `Worst ${TOP_LIMIT} of ${ranked.length} SKUs · ${formatSeats(totals.over_assigned_seats)} people affected`
          : `${formatSeats(totals.over_assigned_seats)} people across ${ranked.length} SKUs`
      }
      since={generatedAt}
      staleAfterMs={STALE_AFTER_MS}
      tableTwin={{
        id: TWIN_ID,
        ariaLabel:
          "SKUs where assignments exceed purchased seats, with assigned, purchased and expiring seat counts",
        data: ranked,
        columns: twinColumns,
        getRowId: (row: LicenseSkuRow) => row.sku_id || row.sku_part_number,
      }}
    >
      {barRows.length === 0 ? (
        <EmptyState
          size="sm"
          title="Nobody loses access when the grace periods close"
          description={`Every assignment fits inside purchased capacity, so the ${formatSeats(totals.grace_period_seats)} expiring seats can lapse without taking a licence from anyone.`}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-fg-muted flex items-start gap-2 text-sm">
            <Icon icon={AlertTriangle} size="sm" tone="danger" />
            <span>
              These SKUs have more people assigned than the tenant currently pays for. The
              difference is being carried by subscriptions that have already expired — when each
              grace period closes, that many people lose the licence. Renew, or reassign them first.
            </span>
          </p>
          <Chart.Container
            config={RISK_CONFIG}
            className="h-64"
            aria-label={`People who lose a licence when the grace period ends, for the ${barRows.length} most exposed SKUs`}
          >
            <Chart.BarChart
              data={barRows}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="name" width={160} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    formatter={(value) =>
                      `${formatSeats(Number(value))} lose the licence when grace ends`
                    }
                  />
                }
              />
              <Chart.Bar
                {...Chart.seriesDefaults}
                dataKey="over_assigned"
                fill="var(--color-over_assigned)"
                radius={[0, 3, 3, 0]}
                cursor="pointer"
                onClick={() => twin.open(TWIN_ID)}
              />
            </Chart.BarChart>
          </Chart.Container>
        </div>
      )}
    </ChartCard>
  );
}
